import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { getFullUrl } from 'hubspot-url-utils';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { useMemo } from 'react';
import { useGetStandardizedInstallOptInQuery } from '../../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/StandardizedInstallOptInRpc.hooks';
import { useGetSatisfyingOptionsQuery } from '../../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { getBreezeStudioUrl, getFormattedUrl, getInstalledAppOverviewUrl } from '../../utils/DetailsPageUrls';
import { getAppInstallType } from '../../utils/getAppInstallType';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../../utils/TrackingUtils';
import { AppUninstallableMessage } from '../AppUninstallableMessage';
import { useAppStatus } from '../hooks/useAppStatus';
import { useAppUninstallableReason } from './useAppUninstallableReason';

//TODO consider stricter typing for each installState, ex: if UNINSTALLABLE force uninstallableReason to be defined
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function useGetInstallConfiguration({
  productType,
  appContext,
  name,
  installUrl,
  sourceId,
  connectionType,
  listingId,
  features: __features,
  published,
  isMcpServer,
  isDashworks,
  isOpsHub,
  isMcpConnector,
  hasMcpServerAccess,
  isStandardizedInstall
}) {
  const {
    trackInteraction,
    isEmbedded,
    previewListingId
  } = useDetailsPageContext();
  const openCopilot = useOpenCopilot();
  const {
    missingScopes,
    uninstallableReason,
    loading: installabilityLoading,
    refetch: refetchInstallability
  } = useAppUninstallableReason({
    appContext,
    features: __features,
    hasMcpServerAccess,
    isMcpServer,
    listingId,
    published
  });

  // TODO: remove opt in logic once standardized install is turned on for all listings permanently
  const {
    data: optInData,
    loading: optInLoading
  } = useGetStandardizedInstallOptInQuery({
    variables: {
      listingId: previewListingId || 0
    },
    skip: !previewListingId || appContext === 'admin-preview' //skip if not in preview
  });
  const isPreviewAndOptedInToStandardInstall = (optInData === null || optInData === void 0 ? void 0 : optInData.optedIn) || false;
  const installType = getAppInstallType({
    connectionType,
    isDashworks,
    isMcpServer,
    isOpsHub,
    isStandardizedInstall,
    isPreviewAndOptedInToStandardInstall
  });
  const reconnectLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "detailsPage.productDetailsCard.cta.reconnect"
  });

  // Fetch app status for installed apps (also fetch for missing permissions to show correct reconnect label)
  const {
    statusData,
    statusLoading,
    statusError,
    pollForStatus
  } = useAppStatus({
    appId: sourceId,
    shouldFetch: appContext === 'app' && ((uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED' || (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_USER_PERMISSIONS')
    // !isPreview, //TODO VERIFY IF NECESSARY
  });
  const appStatusResult = statusData === null || statusData === void 0 ? void 0 : statusData.find(result => result.appId === sourceId);
  const {
    type: statusType,
    cta: {
      link
    } = {}
  } = appStatusResult || {};

  // Interpolate portalId into the reconnect link and ensure it uses app.hubspot.com
  const reconnectUrl = useMemo(() => {
    if (!link) return undefined;
    const portalId = PortalIdParser.get();
    const interpolatedLink = link.replace('${portalId}', String(portalId));
    return `${getFullUrl('app')}${interpolatedLink}`;
  }, [link]);
  const installState = useMemo(() => {
    // TODO: potential to support more than just 'disconnected' status types for RECOMMENDED_ACTION
    if (!statusLoading && !statusError && statusType === 'disconnected') {
      return 'RECOMMENDED_ACTION';
    } else if ((uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED' || installType === 'HOMEMADE_PREINSTALLED') {
      return 'INSTALLED';
    } else if (uninstallableReason) {
      return 'UNINSTALLABLE';
    } else {
      return 'INSTALLABLE';
    }
  }, [uninstallableReason, installType, statusLoading, statusError, statusType]);
  const requestInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: installState === 'RECOMMENDED_ACTION' ? 'detailsPage.productDetailsCard.cta.requestReconnect' : 'detailsPage.productDetailsCard.cta.request'
  });
  const {
    data: missingPlanData
  } = useGetSatisfyingOptionsQuery({
    variables: {
      scopes: missingScopes
    },
    skip: installabilityLoading || missingScopes.length === 0
  });
  const uninstallableTooltipContent = useMemo(() => {
    const missingPlanOptions = missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions;
    if ((uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_HUB_SCOPES' && missingPlanOptions && missingPlanOptions.length === 1 && missingPlanOptions[0].and.length === 1 && missingPlanOptions[0].and[0].or[0].displayName // skip if missing a display name
    ) {
      if (missingPlanOptions[0].and[0].or.length === 1) {
        const option = missingPlanOptions[0].and[0].or[0];
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlan",
          options: {
            planName: option.displayName,
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        });
      } else {
        return /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlans",
            options: {
              productType: unescapedText(`detailsPage.productType.${productType}`)
            }
          }), /*#__PURE__*/_jsx(List, {
            marginBlockStart: "200",
            marginInlineStart: "-400",
            styled: true,
            children: missingPlanOptions[0].and[0].or.map((option, index) => /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.productDetailsCard.cta.uninstallableReasons.orHigherTier",
                options: {
                  planName: option.displayName
                }
              })
            }, index))
          })]
        });
      }
    }
    return /*#__PURE__*/_jsx(AppUninstallableMessage, {
      uninstallableReason: uninstallableReason
    });
  }, [missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions, productType, uninstallableReason]);
  const parsedProductType = isMcpConnector || isMcpServer ? 'CONNECTOR' : productType;
  const installLabelString = `detailsPage.productDetailsCard.cta.install.${parsedProductType}`;
  const installLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: installLabelString
  });
  const installLabelText = I18n.text(installLabelString);
  let postInstallLabel = /*#__PURE__*/_jsx(_Fragment, {});
  let postInstallRoute = undefined;
  let postInstallOnClick = () => {};
  switch (installType) {
    case 'HOMEMADE_DASHWORKS':
      if (isEmbedded) {
        postInstallLabel = /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(SettingsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.productDetailsCard.cta.postInstall.${parsedProductType}`
          })]
        });
        postInstallRoute = getInstalledAppOverviewUrl(sourceId !== null && sourceId !== void 0 ? sourceId : '');
        postInstallOnClick = () => {
          trackInteraction({
            action: 'click manage app button'
          });
          trackCustomMarketplaceOfferingBehavioralEvent('MANAGE_APP_CLICK');
        };
      } else {
        postInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.openBreeze"
        });
        postInstallOnClick = () => {
          openCopilot();
          trackInteraction({
            action: 'click open breeze dashworks button'
          });
        };
      }
      break;
    case 'HOMEMADE_PREINSTALLED':
      postInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.preinstalled",
        options: {
          name
        }
      });
      postInstallRoute = getFormattedUrl(installUrl);
      postInstallOnClick = () => {
        trackInteraction({
          action: 'click preinstalled learn how to use button'
        });
      };
      break;
    case 'HOMEMADE_MCP_SERVER':
      postInstallLabel = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.productDetailsCard.cta.postInstall.CONNECTOR`
      });
      postInstallRoute = getBreezeStudioUrl();
      postInstallOnClick = () => {
        trackInteraction({
          action: 'click connected mcp server button'
        });
        trackCustomMarketplaceOfferingBehavioralEvent('MANAGE_APP_CLICK');
      };
      break;
    default:
      postInstallLabel = /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SettingsIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.postInstall.${parsedProductType}`
        })]
      });
      postInstallRoute = getInstalledAppOverviewUrl(sourceId !== null && sourceId !== void 0 ? sourceId : '');
      postInstallOnClick = () => {
        trackInteraction({
          action: 'click manage app button'
        });
        trackCustomMarketplaceOfferingBehavioralEvent('MANAGE_APP_CLICK');
      };
  }
  return {
    loading: installabilityLoading || statusLoading || optInLoading,
    installState,
    productType,
    installType,
    refetchInstallability,
    //INSTALLABLE
    installLabel,
    installLabelText,
    // UNINSTALLABLE
    uninstallableReason,
    uninstallableTooltipContent,
    requestInstallLabel,
    // INSTALLED
    postInstallLabel,
    postInstallRoute,
    postInstallOnClick,
    // RECOMMENDED_ACTION
    postInstallRecommendedAction: {
      label: reconnectLabel,
      url: reconnectUrl,
      refetch: pollForStatus
    }
  };
}